import {
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { getContrastingTextColor, isTransparentBackground, SiaContrastMode } from '../utils/color-contrast';

@Directive({
  selector: '[colorContrastAuto]',
  standalone: true,
})
export class SiaColorContrastAutoDirective implements OnInit, OnDestroy {
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private ngZone = inject(NgZone);

  // WHY (setter, non plain field): la directive deve poter essere accesa/spenta
  // dinamicamente dal template (es. sia-crud applica il contrasto SOLO ai field
  // con backgroundColor valorizzato). Quando passa a `false` dobbiamo smontare
  // observer/polling e RIMUOVERE un eventuale `color` inline gia' applicato,
  // altrimenti la label resterebbe del colore di contrasto calcolato in
  // precedenza. Quando passa a `true` riattiviamo la logica.
  private _enabled: boolean = true;
  @Input() set colorContrastAuto(value: boolean) {
    const enabled = value !== false;
    if (enabled === this._enabled) {
      return;
    }
    this._enabled = enabled;
    if (!this.#initialized) {
      return;
    }
    if (enabled) {
      this.activate();
    } else {
      this.deactivate();
    }
  }
  get colorContrastAuto(): boolean {
    return this._enabled;
  }

  @Input() contrastMode: SiaContrastMode = 'auto';
  reload = input<boolean>(false);

  #initialized = false;

  #reloadColor = effect(() => {
    if (this.reload() && this._enabled) {
      this.updateTextColor();
    }
  });

  // Minimum contrast ratio to meet (WCAG AA requires 3:1 for large text)
  private minContrastRatio: number = 3.0;

  private observer: MutationObserver | null = null;
  private lastBackgroundColor: string = '';
  private bgPollIntervalId: any = null;

  ngOnInit() {
    this.#initialized = true;
    if (!this._enabled) {
      return;
    }
    this.activate();
  }

  ngOnDestroy() {
    this.deactivate();
  }

  private activate() {
    // Initial color update
    this.updateTextColor();

    // Set up observer to detect background color changes
    this.setupBackgroundObserver();

    // Set up polling as a fallback for inherited/bg changes
    this.setupBackgroundPolling();
  }

  private deactivate() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.bgPollIntervalId) {
      clearInterval(this.bgPollIntervalId);
      this.bgPollIntervalId = null;
    }
    // Rimuove l'eventuale color inline applicato mentre era attiva.
    this.renderer.removeStyle(this.el.nativeElement, 'color');
    this.lastBackgroundColor = '';
  }

  private setupBackgroundObserver() {
    // Use MutationObserver to watch for style attribute changes
    this.ngZone.runOutsideAngular(() => {
      this.observer = new MutationObserver((mutations) => {
        let backgroundChanged = false;

        for (const mutation of mutations) {
          if (
            mutation.type === 'attributes' &&
            (mutation.attributeName === 'style' || mutation.attributeName === 'class')
          ) {
            backgroundChanged = true;
            break;
          }
        }

        if (backgroundChanged) {
          const currentBgColor = this.getBackgroundColor();
          if (currentBgColor !== this.lastBackgroundColor) {
            this.lastBackgroundColor = currentBgColor;

            // Run inside Angular zone when updating DOM
            this.ngZone.run(() => {
              this.updateTextColor();
            });
          }
        }
      });

      this.observer.observe(this.el.nativeElement, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      });

      // Store initial background color
      this.lastBackgroundColor = this.getBackgroundColor();
    });
  }

  // Polling fallback for background color changes (handles inherited/bg changes)
  private setupBackgroundPolling() {
    this.ngZone.runOutsideAngular(() => {
      this.bgPollIntervalId = setInterval(() => {
        const currentBgColor = this.getBackgroundColor();
        if (currentBgColor !== this.lastBackgroundColor) {
          this.lastBackgroundColor = currentBgColor;
          this.ngZone.run(() => {
            this.updateTextColor();
          });
        }
      }, 1000);
    });
  }

  private updateTextColor() {
    const bgColor = this.getBackgroundColor();
    // Guardia robustezza (Fix B): se lo sfondo calcolato e' di fatto assente
    // (alpha 0, `transparent`, non risolvibile) NON applichiamo alcun colore,
    // altrimenti `rgba(0,0,0,0)` verrebbe letto come nero e produrrebbe testo
    // bianco a contrasto, sovrascrivendo il colore ereditato dal tema.
    if (isTransparentBackground(bgColor)) {
      this.renderer.removeStyle(this.el.nativeElement, 'color');
      return;
    }
    // Logica WCAG estratta in `getContrastingTextColor` (sia-shared/utils):
    // stesso algoritmo, condiviso con i widget dashboard ECharts per DRY.
    const textColor = getContrastingTextColor(bgColor, this.contrastMode, this.minContrastRatio);

    this.renderer.setStyle(this.el.nativeElement, 'color', textColor);
  }

  private getBackgroundColor(): string {
    return (
      getComputedStyle(this.el.nativeElement).backgroundColor ?? getComputedStyle(this.el.nativeElement).background
    );
  }

  private calculateLuminance(color: string): number {
    const rgb = this.parseColor(color);
    const [r, g, b] = rgb.map((c) => {
      const normalized = c / 255;
      return normalized <= 0.03928 ? normalized / 12.92 : Math.pow((normalized + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  private parseColor(colorStr: string): number[] {
    if (colorStr.startsWith('#')) {
      return this.hexToRgb(colorStr);
    }
    const rgbMatch = colorStr.match(/(\d+),\s*(\d+),\s*(\d+)/);
    return rgbMatch ? [parseInt(rgbMatch[1]), parseInt(rgbMatch[2]), parseInt(rgbMatch[3])] : [0, 0, 0];
  }

  private hexToRgb(hex: string): number[] {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (_, r, g, b) => r + r + g + g + b + b);
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : [0, 0, 0];
  }

  // Convert RGB array to hex string (without #)
  private rgbToHex(rgb: number[]): string {
    return rgb
      .map((x) => {
        const hex = Math.max(0, Math.min(255, x)).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
      })
      .join('');
  }

  // Darken a color by the specified percentage with enhanced contrast
  private shadeColor(hexcolor: string): string {
    // Remove # if present
    hexcolor = hexcolor.replace('#', '');

    const r = parseInt(hexcolor.substr(0, 2), 16);
    const g = parseInt(hexcolor.substr(2, 2), 16);
    const b = parseInt(hexcolor.substr(4, 2), 16);

    // Convert to HSL for better color manipulation
    const hsl = this.rgbToHsl(r, g, b);
    const bgLuminance = this.relativeLuminance([r, g, b]);

    // If the color is achromatic (gray/white/black), keep it gray
    let newHue = hsl[0];
    let newSaturation = hsl[1];
    if (hsl[1] < 0.1) {
      newHue = 0; // hue doesn't matter for gray
      newSaturation = 0; // keep it gray
    } else {
      // For already colorful backgrounds, enhance saturation
      newSaturation = Math.min(1, hsl[1] * 1.5);
    }

    // Start with a reasonable darkening
    let newLightness = Math.max(0.1, hsl[2] * 0.4); // More aggressive darkening

    // Convert to RGB to check contrast
    let rgb = this.hslToRgb(newHue, newSaturation, newLightness);
    let rgbNormalized = rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))));
    let textLuminance = this.relativeLuminance(rgbNormalized);

    // Ensure we meet minimum contrast ratio
    let contrastRatio = this.calculateContrastRatio(bgLuminance, textLuminance);

    // If contrast isn't sufficient, adjust lightness until it is
    if (contrastRatio < this.minContrastRatio) {
      // Binary search to find appropriate lightness
      let minL = 0;
      let maxL = Math.min(hsl[2], 0.5); // Don't go lighter than the background

      for (let attempts = 0; attempts < 8 && contrastRatio < this.minContrastRatio; attempts++) {
        // Try a lightness halfway between min and max
        newLightness = (minL + maxL) / 2;

        rgb = this.hslToRgb(newHue, newSaturation, newLightness);
        rgbNormalized = rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))));
        textLuminance = this.relativeLuminance(rgbNormalized);
        contrastRatio = this.calculateContrastRatio(bgLuminance, textLuminance);

        if (contrastRatio >= this.minContrastRatio) {
          // We've found a good lightness, but is there a better one?
          minL = newLightness;
        } else {
          // This lightness is too bright, try darker
          maxL = newLightness;
        }
      }

      // If we still can't achieve contrast, go to black as fallback
      if (contrastRatio < this.minContrastRatio) {
        return '000000';
      }
    }

    // Convert final values to RGB
    rgb = this.hslToRgb(newHue, newSaturation, newLightness);

    // Ensure RGB values are in valid range and convert to hex
    return rgb
      .map((v) => {
        const colorValue = Math.max(0, Math.min(255, Math.round(v)));
        return colorValue.toString(16).padStart(2, '0');
      })
      .join('');
  }

  // Lighten a color by the specified percentage with enhanced contrast
  private tintColor(hexcolor: string): string {
    // Remove # if present
    hexcolor = hexcolor.replace('#', '');

    const r = parseInt(hexcolor.substr(0, 2), 16);
    const g = parseInt(hexcolor.substr(2, 2), 16);
    const b = parseInt(hexcolor.substr(4, 2), 16);

    // Convert to HSL for better color manipulation
    const hsl = this.rgbToHsl(r, g, b);
    const bgLuminance = this.relativeLuminance([r, g, b]);

    // If the color is achromatic (gray/white/black), keep it gray
    let newHue = hsl[0];
    let newSaturation = hsl[1];
    if (hsl[1] < 0.1) {
      newHue = 0; // hue doesn't matter for gray
      newSaturation = 0; // keep it gray
    } else {
      // For already colorful backgrounds, enhance saturation
      newSaturation = Math.min(1, hsl[1] * 1.5);
    }

    // Start with a reasonable lightening
    let newLightness = Math.min(0.9, 0.6 + hsl[2] * 0.4); // More aggressive lightening

    // Convert to RGB to check contrast
    let rgb = this.hslToRgb(newHue, newSaturation, newLightness);
    let rgbNormalized = rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))));
    let textLuminance = this.relativeLuminance(rgbNormalized);

    // Ensure we meet minimum contrast ratio
    let contrastRatio = this.calculateContrastRatio(bgLuminance, textLuminance);

    // If contrast isn't sufficient, adjust lightness until it is
    if (contrastRatio < this.minContrastRatio) {
      // Binary search to find appropriate lightness
      let minL = Math.max(hsl[2], 0.5); // Don't go darker than the background
      let maxL = 1;

      for (let attempts = 0; attempts < 8 && contrastRatio < this.minContrastRatio; attempts++) {
        // Try a lightness halfway between min and max
        newLightness = (minL + maxL) / 2;

        rgb = this.hslToRgb(newHue, newSaturation, newLightness);
        rgbNormalized = rgb.map((v) => Math.max(0, Math.min(255, Math.round(v))));
        textLuminance = this.relativeLuminance(rgbNormalized);
        contrastRatio = this.calculateContrastRatio(bgLuminance, textLuminance);

        if (contrastRatio >= this.minContrastRatio) {
          // We've found a good lightness, but is there a better one?
          maxL = newLightness;
        } else {
          // This lightness is not bright enough, try brighter
          minL = newLightness;
        }
      }

      // If we still can't achieve contrast, go to white as fallback
      if (contrastRatio < this.minContrastRatio) {
        return 'ffffff';
      }
    }

    // Convert final values to RGB
    rgb = this.hslToRgb(newHue, newSaturation, newLightness);

    // Ensure RGB values are in valid range and convert to hex
    return rgb
      .map((v) => {
        const colorValue = Math.max(0, Math.min(255, Math.round(v)));
        return colorValue.toString(16).padStart(2, '0');
      })
      .join('');
  }

  // Calculate the relative luminance of a color (WCAG formula)
  private relativeLuminance(rgb: number[]): number {
    // Normalize RGB values to 0-1 range
    const [r, g, b] = rgb.map((v) => {
      const normalized = v / 255;
      // Apply sRGB gamma correction
      return normalized <= 0.03928 ? normalized / 12.92 : Math.pow((normalized + 0.055) / 1.055, 2.4);
    });

    // WCAG relative luminance formula
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  }

  // Calculate contrast ratio between two luminance values
  private calculateContrastRatio(luminance1: number, luminance2: number): number {
    const lighter = Math.max(luminance1, luminance2);
    const darker = Math.min(luminance1, luminance2);
    return (lighter + 0.05) / (darker + 0.05);
  }

  // Convert RGB to HSL
  private rgbToHsl(r: number, g: number, b: number): [number, number, number] {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0,
      s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
      }

      h /= 6;
    }

    return [h, s, l];
  }

  // Convert HSL to RGB
  private hslToRgb(h: number, s: number, l: number): [number, number, number] {
    let r, g, b;

    if (s === 0) {
      r = g = b = l; // achromatic
    } else {
      const hue2rgb = (p: number, q: number, t: number) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1 / 6) return p + (q - p) * 6 * t;
        if (t < 1 / 2) return q;
        if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      r = hue2rgb(p, q, h + 1 / 3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1 / 3);
    }

    return [r * 255, g * 255, b * 255];
  }
}
